import { useEffect, useState } from 'react'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shared/ui/dialog'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'

/**
 * Corregir el correo de la cuenta.
 *
 * Existe porque sin esto un correo mal tipeado deja la cuenta MUERTA y sin salida: el
 * código de acceso viaja a un buzón que no existe, la cuenta no se puede borrar, y como
 * el correo es único, abrir otra deja huérfana a la primera con sus accesos.
 *
 * El diálogo dice ANTES que se cierran las sesiones. No es un detalle: el correo es el
 * factor de autenticación, así que cambiarlo sin cerrar sesiones dejaría adentro a quien
 * tenía el buzón anterior — que es exactamente el caso que motiva la corrección.
 */
type ClientAccountEmailDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentEmail: string
  isPending: boolean
  onSubmit: (email: string) => void
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function ClientAccountEmailDialog({
  open,
  onOpenChange,
  currentEmail,
  isPending,
  onSubmit
}: ClientAccountEmailDialogProps) {
  const [email, setEmail] = useState(currentEmail)
  const [touched, setTouched] = useState(false)

  // Reabrir el diálogo lo devuelve al correo vigente: sin esto, un intento cancelado deja
  // el valor a medio escribir la próxima vez que alguien lo abre.
  useEffect(() => {
    if (open) {
      setEmail(currentEmail)
      setTouched(false)
    }
  }, [open, currentEmail])

  const normalized = email.trim().toLowerCase()
  const invalid = normalized !== '' && !EMAIL_PATTERN.test(normalized)
  const unchanged = normalized === currentEmail.toLowerCase()
  const canSubmit = normalized !== '' && !invalid && !unchanged && !isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Corregir el correo de la cuenta</DialogTitle>
          <DialogDescription>
            Es el correo al que llega el código para entrar a la aplicación. Al cambiarlo se
            cerrarán las sesiones abiertas del cliente.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(event) => {
            event.preventDefault()
            if (!canSubmit) return
            onSubmit(normalized)
          }}
        >
          <Field data-invalid={touched && invalid}>
            <FieldLabel htmlFor="client-account-email">Correo</FieldLabel>
            <Input
              id="client-account-email"
              type="email"
              autoComplete="off"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
              }}
              onBlur={() => {
                setTouched(true)
              }}
            />
            <FieldDescription>
              El correo actual es <strong>{currentEmail}</strong>.
            </FieldDescription>
            {touched && invalid ? (
              <FieldError errors={[{ message: 'Escribe un correo válido.' }]} />
            ) : null}
          </Field>

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false)
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isPending ? 'Guardando…' : 'Guardar y cerrar sesiones'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
